import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {
  endPasswordChange,
  selectHashedPassword,
  selectIsPasswordChangePending,
  selectPasswordChangeToken,
  selectPropertiesApp,
  selectRefreshToken,
  selectSession,
  useLogoutMutation,
  useResetPasswordMutation,
} from '../../redux';
import { ChangePasswordCredential } from '../../types';
import { PROPERTY_KEYS } from '../../config';
import { toHttps } from '../../utils/serviceUrl';
import { resolveResetError } from '../../utils/resetPasswordError';
import sha256 from 'crypto-js/sha256';
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  hasSpecialCharacter,
  hasUppercase,
} from '../../utils/passwordPolicy';

export interface ResetPasswordFormFields {
  actualPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const keyPrefix = 'hooks.formManagements.useResetPasswordFormManagement';

const defaultValues: ResetPasswordFormFields = {
  actualPassword: '****',
  newPassword: '',
  confirmPassword: '',
};

export const useResetPasswordFormManagement = () => {
  const [fieldsValidationErr, setFieldsValidationErr] = useState<
    string | null
  >();
  const [genericErr, setGenericErr] = useState<string | null>();
  const { t } = useTranslation('translation', { keyPrefix });
  const dispatch = useDispatch();
  const session = useSelector(selectSession);
  const actualHashedPassword = useSelector(selectHashedPassword);
  const properties = useSelector(selectPropertiesApp);
  /**
   * Las dos entradas comparten formulario y se distinguen aquí: con el token
   * del `422` y sin sesión es el cambio obligatorio; con sesión, el
   * voluntario desde Perfil.
   */
  const isPasswordChangePending = useSelector(selectIsPasswordChangePending);
  const passwordChangeToken = useSelector(selectPasswordChangeToken);
  const refreshToken = useSelector(selectRefreshToken);

  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  const [logoutMutation] = useLogoutMutation();

  const schema = yup.object().shape({
    newPassword: yup
      .string()
      .required(t('schema.newPassword.required'))
      .min(PASSWORD_MIN_LENGTH, t('schema.newPassword.min'))
      .max(PASSWORD_MAX_LENGTH, t('schema.newPassword.max'))
      .test('uppercase', t('schema.newPassword.uppercase'), value =>
        hasUppercase(value ?? '')
      )
      .test('special', t('schema.newPassword.special'), value =>
        hasSpecialCharacter(value ?? '')
      ),
    confirmPassword: yup
      .string()
      .required(t('schema.confirmPassword.required'))
      .oneOf(
        [yup.ref('newPassword'), null],
        t('schema.confirmPassword.confirmation')
      ),
  });

  const methods = useForm<ResetPasswordFormFields>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    const { formState: errors } = methods;
    setFieldsValidationErr(
      errors.errors.newPassword?.message ||
        errors.errors.confirmPassword?.message ||
        null
    );
    // eslint-disable-next-line
  }, [methods.formState.errors]);

  const credential = (): ChangePasswordCredential | null => {
    if (isPasswordChangePending && passwordChangeToken) {
      return { kind: 'passwordChangeToken', passwordChangeToken };
    }
    if (refreshToken) {
      return {
        kind: 'session',
        refreshToken,
        sessionToken: session.sessionToken ?? null,
      };
    }
    return null;
  };

  const submit: SubmitHandler<ResetPasswordFormFields> = async fields => {
    clearErrorMessages();

    const url = properties[PROPERTY_KEYS.CHANGE_PASSWORD];
    if (!url) {
      setGenericErr(t('errors.connection'));
      return;
    }

    /**
     * Se compara contra la contraseña con la que se entró: la temporal en el
     * cambio obligatorio, la vigente en el voluntario. Por eso el texto es
     * distinto en cada entrada — HU-CUDL-089 / CA-08.
     */
    const newHashedPassword = sha256(fields.newPassword).toString();
    if (actualHashedPassword === newHashedPassword) {
      setGenericErr(
        t(isPasswordChangePending ? 'errors.samePass' : 'errors.sameAsCurrent')
      );
      return;
    }

    /**
     * Sin credencial —una sesión persistida de antes de que la Web guardara el
     * `refreshToken`— el servicio respondería `401`. Se avisa lo mismo que
     * diría él, sin gastar la petición.
     */
    const authorization = credential();
    if (!authorization) {
      setGenericErr(t('errors.sessionExpired'));
      return;
    }

    try {
      await resetPassword({
        credential: authorization,
        password: fields.newPassword,
        url: toHttps(url),
      }).unwrap();
    } catch (err) {
      // El vendedor se queda en la pantalla con lo que tecleó — CA-09.
      setGenericErr(resolveResetError(err, t));
      return;
    }

    // Las dos entradas terminan en el ingreso, sin sesión — CA-12.
    if (isPasswordChangePending) {
      dispatch(endPasswordChange());
      return;
    }
    await logoutMutation(session.username!);
  };

  const clearErrorMessages = (): void => {
    setGenericErr(null);
    setFieldsValidationErr(null);
  };

  /**
   * Desistir del cambio obligatorio devuelve al ingreso. No hay sesión que
   * cerrar: basta con soltar el token del `422`.
   */
  const cancel = (): void => {
    dispatch(endPasswordChange());
  };

  return {
    loading: isLoading,
    fieldsValidationErr,
    genericErr,
    methods,
    clearErrorMessages,
    cancel,
    isPasswordChangePending,
    submit,
  };
};
